/**
 * 🖐️ Your own hand, and the swing that says you are working (T150 piece ①).
 *
 * 🎯 **haugur, 2026-08-17, after hold-to-break shipped with feedback he could
 * not see:** *"maybe look into the mincecraft dosc regard breaking blocks, they
 * have a visible hand that moves when you chop."* Then, choosing what ours is
 * made of: *"32 sleeve."*
 *
 * ## What his reference pictures actually showed
 *
 * He sent three Minecraft frames — a diamond sword, and a bare hand twice.
 * 🔴 **The useful finding is how LITTLE there is:** the bare hand is one tapered
 * box in flat skin colour, **no fingers and no detail whatever**, sitting in the
 * bottom-right at a diagonal. **So this is not a modelling job**, which is the
 * only reason it is affordable at all.
 *
 * ⚠️ **And it is always on screen, not only while chopping.** That is what makes
 * a swing read as *your* action rather than as something happening to the world.
 *
 * ## Ours is a sleeve, because ours is a person in Iceland in February
 *
 * A bare forearm is a summer game's answer. **The sleeve is undyed Icelandic
 * wool** — the *sauðalitir*, the natural colours of the sheep, which are the only
 * colours a settler's clothing could be — with a hand at the end of it.
 *
 * ## ⚠️ One honest limit, named rather than hidden
 *
 * ✅ **It darkens at night now — T150 piece ④, 2026-08-19.** It was flat-lit and
 * therefore the brightest thing on screen at 03:00.
 *
 * 🔴 **It is dimmed, not lit, and the difference matters.** A real light rig
 * would shade the hand by the sun's *direction* — so turning on the spot would
 * brighten and darken it as you faced toward or away from the sun, which reads
 * as flickering rather than as light. **Minecraft lights the held item by the
 * light LEVEL where the player stands**, and that is the honest model: how
 * bright is it *here*, not which way is the sun. `main.ts` computes the level;
 * this only applies it.
 *
 * ⚠️ **`depthTest: false`.** The hand sits centimetres from the eye, so a block
 * you walk up against would otherwise swallow it. **Drawn last, over
 * everything**, which is what every first-person game does with the held item.
 */

import type { Color } from 'three';
import { BoxGeometry, Group, Mesh, MeshBasicMaterial } from 'three';
import { heldItemObject, type HeldObject } from './helditem.ts';
import type { SlotContent } from './hud-parts.ts';

/**
 * ⏱️ **Real seconds for one complete swing.** Fast enough to read as a blow
 * rather than a wave, slow enough to see at 30 fps.
 */
const SWING_SECONDS = 0.35;

/**
 * The arc of one swing, as a fraction of the way through it.
 *
 * ✅ **Pure, exported and tested**, so the *motion* can be reasoned about
 * without a renderer — the same split `breaking.ts` uses for the stopwatch.
 *
 * ⚠️ **Not a sine.** A sine spends as long coming back as going down, which
 * reads as waving. A blow is **fast down and slower back**, so the strike takes
 * the first third and the recovery the rest.
 *
 * @param t 0…1 through the swing
 * @returns 0 at rest, 1 at the bottom of the blow
 */
export function swingArc(t: number): number {
  const clamped = Math.min(1, Math.max(0, t));
  const STRIKE = 0.33;
  if (clamped < STRIKE) {
    /* Down: accelerating, so it lands rather than drifts. */
    const p = clamped / STRIKE;
    return p * p;
  }
  /* Back up: eased, and never quite snapping to zero. */
  const p = (clamped - STRIKE) / (1 - STRIKE);
  return (1 - p) * (1 - p);
}

/** Undyed Icelandic wool — *mórautt*, the brown of the sheep, not a dye. */
const SLEEVE = 0x6b5b4a;
/** The hand itself. Deliberately muted; a pink hand reads as plastic. */
const SKIN = 0xc9a686;

/**
 * ⚠️ **How dark the hand is allowed to get, 0…1.**
 *
 * 🔴 **Not zero, and this is a judgement rather than a measurement.** T116
 * retired the artificial night floor and gave the world **the real dark**, so
 * the honest answer here is very nearly black. But **a hand that is pure black
 * is indistinguishable from a hand that failed to draw**, and this project has
 * already lost a day to a working feature that looked broken. A trace of it
 * remains.
 */
const MIN_LIGHT = 0.12;

/**
 * 🔴 **FOUR TOOL COLOURS AND A THREE-WAY UNION USED TO LIVE HERE. BOTH ARE GONE.**
 *
 * The held tool was built out of boxes, so this file carried its own copies of
 * the shaft and head colours — *"two copies of four colours, and that is a real
 * risk"*, as the note here admitted at the time — and its own idea of what could
 * be held (*empty*, *tool*, *block*).
 *
 * ✅ **Both risks are removed rather than managed.** The hand is handed a
 * finished object built from the pack's own drawing, so **there is nothing here
 * to drift** and **no list of kinds to keep in step with the items file.**
 */

/**
 * 📐 **How big a carried animal is drawn in your own hands** — T261.
 *
 * ⚠️ **A DIAL.** The photograph is trimmed to the animal's own box and fills its
 * square, unlike a tool's, so it needs bringing down to an armful.
 */
const CARRY_SCALE = 0.55;

export class Hand {
  /** Add this to the camera, not to the scene — it travels with the eye. */
  readonly object: Group;
  /** 🖐️ The bare sleeve and hand — shown ONLY when holding nothing. */
  private readonly bare: Group;
  /** 🖐️ Whatever is being held, built from the pack's own drawing. */
  private readonly held: Group;
  /** What is on screen, so an unchanged frame writes nothing. */
  private shown = 'empty';
  /**
   * The held item's own materials, kept apart from {@link Hand.lit}.
   *
   * ⚠️ **Separate because they come and go.** The sleeve's materials live for
   * the life of the page; a held item's are replaced every time the selected
   * slot changes, and mixing them into one list would grow it without bound.
   */
  private heldLit: readonly { readonly material: MeshBasicMaterial; readonly base: Color }[] = [];
  /**
   * 🌙 A photographed item's own dimmer.
   *
   * ⚠️ **A node material ignores `material.color`**, so a photograph cannot be
   * dimmed through {@link Hand.heldLit} at all — it hands back this instead.
   */
  private heldDim: ((level: number) => void) | undefined = undefined;
  /**
   * What was last asked for, so {@link Hand.refresh} can ask again.
   *
   * 🔴 **`shown` is a GUARD, and a guard that says *nothing changed* is wrong
   * the moment the ANSWER changes underneath it.** The photographs decode after
   * the first frame, and without this the hand kept handing back the drawing it
   * had cached before they landed — a picture that was right when it was made
   * and stale a moment later.
   */
  private lastContent: SlotContent | null = null;
  private swinging = false;
  /** Seconds into the current swing. Runs continuously while held down. */
  private phase = 0;
  /** ⚠️ **No longer `readonly`** — {@link Hand.setCarrying} moves the hand, and
      the swing rests from wherever it now is. */
  private restY: number;
  /** 🙌 Whether the hand is posed for an armful rather than a tool — T261. */
  private carryingPose = false;
  /** 🐣 The size last drawn, so a lamb growing in your arms is noticed. */
  private carriedSize = 1;
  /** The materials to dim, with the colour each was authored at. */
  private readonly lit: { material: MeshBasicMaterial; base: Color }[] = [];
  /** The level last applied, so an unchanged frame writes nothing. */
  private light = -1;

  constructor() {
    this.object = new Group();

    const sleeve = new Mesh(
      new BoxGeometry(0.13, 0.34, 0.13),
      new MeshBasicMaterial({ color: SLEEVE, depthTest: false, depthWrite: false }),
    );
    sleeve.position.set(0, -0.06, 0);

    const hand = new Mesh(
      new BoxGeometry(0.12, 0.13, 0.12),
      new MeshBasicMaterial({ color: SKIN, depthTest: false, depthWrite: false }),
    );
    hand.position.set(0, 0.16, 0);

    /* Drawn after the world and after the break box, since none of it is
       depth-tested and the hand must be in front of both. */
    sleeve.renderOrder = 1000;
    hand.renderOrder = 1001;
    /* 🖐️ Grouped, because the whole arm goes away the moment you hold
       something — see {@link Hand.setHeld}. */
    this.bare = new Group();
    this.bare.add(sleeve, hand);
    this.object.add(this.bare);
    for (const mesh of [sleeve, hand]) {
      this.lit.push({ material: mesh.material, base: mesh.material.color.clone() });
    }

    /**
     * 🖐️ **Where the held item goes.** Empty until something is put in it.
     *
     * 🎯 **haugur, 2026-09-02, on the boxes this replaced:** *"pics of shovel
     * and axe and pick and blocks in hand, still looks funky."*
     *
     * 🔴 **It holds the PACK'S OWN DRAWING, built as triangles** — see
     * `helditem.ts` for why triangles and not a texture. All three tools were
     * one mallet before this; now an axe is the axe haugur approved.
     *
     * 🔴 **Low and to the right, and this is the number he sent a photograph to
     * correct.** The first attempt sat above the group's origin and landed at
     * eye level, in front of what you were trying to look at.
     */
    this.held = new Group();
    this.held.position.set(0.04, 0.02, 0.04);
    /* Straightens the drawing against the group's tilt, so it reads upright
       while the whole thing still swings on the group's own arc. */
    this.held.rotation.set(0.35, -0.25, -0.3);
    this.held.visible = false;
    this.object.add(this.held);

    /**
     * 🖐️ **Into the bottom-right CORNER**, angled up toward the crosshair.
     *
     * 🎯 **haugur, 2026-09-04:** *"you could move everything, also the empty
     * hand further right and down to the corner, that gives the player a
     * clearer view of the world, these things don have to be in the middle of
     * screen."*
     *
     * ⚠️ **In camera space the eye looks down −Z, so −Z is "in front".**
     *
     * 🔴 **These are not guessed — they are worked out from the camera.** The
     * lens is **75° vertical** (`main.ts`), so at 0.62 m in front the screen is
     * `0.62 · tan 37.5° = 0.476 m` from the middle to the top edge, and as much
     * again times the aspect to the side. **The old `0.3, −0.28` put the hand
     * 63 % of the way to the right edge and 59 % of the way down**; these put it
     * at about 78 % and 76 %, which is the corner without falling out of it.
     *
     * ⚠️ **The whole GROUP moves, so the bare hand goes with the tool** — which
     * is what he asked for, and they must never drift apart.
     */
    this.object.position.set(0.44, -0.36, -0.62);
    this.object.rotation.set(-0.35, 0.25, 0.4);
    this.restY = this.object.position.y;
  }

  /**
   * 🙌 **An ARMFUL sits differently from a tool** — T261.
   *
   * 🔴 **FOUND BY BAKING A PICTURE, and by nothing else.** The sheep went into
   * the hand through the photograph path the tools already use — which worked
   * first time — **and then filled the bottom-right corner with half the animal
   * off the edge of the screen.**
   *
   * 📏 **The corner is the right place for a thin diagonal tool.** An axe's
   * picture is mostly transparent; the sheep's is trimmed to its own bounding
   * box and **fills its whole square**, so the same numbers put an armful where
   * a handle used to be.
   *
   * ✅ **So carrying gets its own placement**: nearer the middle, lower, level,
   * and a little smaller. 🎯 haugur's own rule for the corner still holds for
   * everything else — *"these things don't have to be in the middle of
   * screen"* — **and an animal in both arms is the one thing that does.**
   *
   * ⚠️ **Every number here is a DIAL and his eye decides them.**
   */
  setCarrying(
    on: boolean,
    /** 🐣 Protocol 49 — a lamb's size as a fraction of an adult; 1 for a grown animal. */
    size = 1,
  ): void {
    if (on === this.carryingPose && size === this.carriedSize) return;
    this.carryingPose = on;
    this.carriedSize = size;
    if (on) {
      /**
       * 📐 **The CORNER is kept — it is haugur's own instruction** — but raised
       * and shrunk until a square picture fits inside it.
       *
       * 🎯 *"you could move everything, also the empty hand further right and
       * down to the corner, that gives the player a clearer view of the
       * world."*
       *
       * ⚠️ **Two bakes to get here, and both faults were invisible in every
       * number.** At the tool's own numbers the sheep hung half off the
       * bottom-right edge; moved to the middle it sat behind the slot bar. 📏
       * **At 0.62 m in front the screen is 0.476 m from the middle to the top
       * edge**, so  is 76% of the way down and a 0.46 m square centred
       * there runs off the bottom.  is 63%, and the shrunk picture ends
       * above the bar.
       */
      this.object.position.set(0.44, -0.26, -0.62);
      /* 🐑 Held level. The tool's tilt is a wrist; two arms round an animal
         are not. */
      this.object.rotation.set(-0.12, 0.06, 0.05);
      this.held.position.set(0, 0, 0);
      this.held.rotation.set(0, 0, 0);
      /* 🐣 A lamb at a lamb's size — 🎯 haugur: *"yes fix the carried lamb too."* */
      this.held.scale.setScalar(CARRY_SCALE * size);
    } else {
      this.object.position.set(0.44, -0.36, -0.62);
      this.object.rotation.set(-0.35, 0.25, 0.4);
      this.held.position.set(0.04, 0.02, 0.04);
      this.held.rotation.set(0.35, -0.25, -0.3);
      this.held.scale.setScalar(1);
    }
    this.restY = this.object.position.y;
  }

  /**
   * 🖐️ **Show whatever is in the selected slot** — the pack's own drawing of it,
   * or your bare hand when there is nothing.
   *
   * 🎯 **haugur, 2026-09-01:** *"if no tool then hand, if holding a pickaxe, then
   * pickaxe, when holding a block then the block etcetc."* Then, on the boxes
   * that first answered that: *"still looks funky."*
   *
   * ✅ **Checked rather than assumed: vanilla Minecraft shows ONLY the item and
   * no arm at all** — the floating item is the real behaviour, and there are mods
   * that exist purely to put hands back. ⚠️ **The research library could not have
   * saved the session that got this wrong**; that gap is now
   * `11_The_First_Hour.md` §4a.
   *
   * 🔴 **The hand is the fallback for EVERY failure, and that is deliberate.**
   * `heldItemObject` returns `null` for an item nobody drew, for a block with no
   * colours, and for a drawing that will not build — and the hand is what you
   * hold everything else *with*, so it is the right answer to all three.
   */
  setHeld(content: SlotContent | null): void {
    const key = content === null ? 'empty' : `${content.item ?? ''}|${content.colour ?? ''}`;
    this.lastContent = content;
    if (key === this.shown) return;
    this.shown = key;

    /* ⚠️ Detached, not disposed. The geometry is cached and shared by
       `helditem.ts`, so the next time this item is selected the same object is
       handed back — disposing it here would break every later use of it. */
    this.held.clear();
    this.heldLit = [];
    this.heldDim = undefined;

    const built: HeldObject | null = content === null ? null : heldItemObject(content);
    if (built === null) {
      /* 🔴 Bare hand, and nothing else visible. */
      this.held.visible = false;
      this.bare.visible = true;
      return;
    }

    this.held.add(built.object);
    this.held.visible = true;
    this.bare.visible = false;
    this.heldLit = built.lit;
    this.heldDim = built.dim;
    /* Apply the light the player is standing in at once, or the item is drawn at
       full noon brightness until the sun next moves. */
    this.applyLight();
  }

  /**
   * Multiply every material by the current light level.
   *
   * 🔴 **Always from the colour each was AUTHORED at**, never from what it is
   * showing. Dimming an already-dimmed colour compounds, and the hand walks
   * itself to black over a few minutes of dusk.
   */
  private applyLight(): void {
    const level = Math.min(1, Math.max(MIN_LIGHT, this.light));
    for (const { material, base } of [...this.lit, ...this.heldLit]) {
      material.color.copy(base).multiplyScalar(level);
    }
    /* 📷 And the photograph, which has no `color` to write. */
    this.heldDim?.(level);
  }

  /**
   * 🌙 How bright it is **where the player is standing**, 0…1 (T150 piece ④).
   *
   * Multiplied into the authored colours rather than replacing them, so the
   * sleeve stays *mórautt* and the hand stays skin — a dark hand, not a grey
   * one.
   *
   * ⚠️ **Called every frame, and returns immediately when nothing moved.**
   * Writing a `Color` marks the material for upload; doing that sixty times a
   * second for an unchanged value is the cost this guard exists to avoid.
   */
  setLight(level: number): void {
    const clamped = Math.min(1, Math.max(MIN_LIGHT, level));
    /* A hundredth of a stop is far below what anybody can see, and the light
       changes continuously all day — without this the guard never fires. */
    if (Math.abs(clamped - this.light) < 0.01) return;
    this.light = clamped;
    /* 🔴 Through `applyLight`, so the HELD item is dimmed too. It used to walk
       `this.lit` alone, and a held axe would have stayed noon-bright at three in
       the morning — the exact fault T150 piece ④ fixed for the sleeve. */
    this.applyLight();
  }

  /**
   * 📷 **Build the held item again, because the picture for it has changed.**
   *
   * 🎯 **haugur, 2026-09-04:** *"they have just been put in as images on menus,
   * not in the hud itself."* The photographs are decoded after start-up, and
   * everything downstream of that had a guard saying *this has not changed*:
   * `helditem.ts` caches by item key, and {@link Hand.setHeld} returns early
   * when the key matches what is already shown.
   *
   * ⚠️ **Clearing the first cache alone did nothing** — the second guard still
   * refused. **This is the same fault the traps file names three times over: a
   * second way in forgetting what the first one does.**
   */
  refresh(): void {
    this.shown = '';
    this.setHeld(this.lastContent);
  }

  /** Start or stop swinging. Called with whether the player is mid-break. */
  setSwinging(swinging: boolean): void {
    this.swinging = swinging;
  }

  /**
   * Advance the swing.
   *
   * ⚠️ **The swing finishes even after the button comes up.** Cutting it off
   * mid-blow reads as a dropped frame; letting the arc complete costs a third of
   * a second and looks deliberate.
   */
  update(dt: number): void {
    if (this.swinging) {
      this.phase = (this.phase + dt / SWING_SECONDS) % 1;
    } else if (this.phase > 0) {
      this.phase = Math.min(1, this.phase + dt / SWING_SECONDS);
      if (this.phase >= 1) this.phase = 0;
    }

    const arc = swingArc(this.phase);
    /* Down and slightly forward — a blow, not a wave. */
    this.object.position.y = this.restY - 0.16 * arc;
    this.object.rotation.x = -0.35 - 0.9 * arc;
  }
}
